import React, { useState, useCallback } from "react";
import { Dropdown } from "react-bootstrap";
import { FormattedMessage } from "react-intl";
import "./OwHierarchicalCategorySelector.scss";

import IOwHierarchicalCategorySelectorProps from "./IOwHierarchicalCategorySelectorProps";
import ICategory from "../../../models/ICategory";
import IOnGetSubCategoriesDTO from "../../../models/DTO/IOnGetSubCategoriesDTO";
import RestEndpointsEnum from "../../../models/RestEndpointsEnum";
import { buildRestUrl, buildAuthHeadersForFetch } from "../../../services/RestService";

const OwHierarchicalCategorySelector = (props: IOwHierarchicalCategorySelectorProps) => {
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [navigationPath, setNavigationPath] = useState<ICategory[]>([]);
    const [currentCategories, setCurrentCategories] = useState<ICategory[]>(props.categories);
    const [loadingSubcategories, setLoadingSubcategories] = useState<boolean>(false);

    const fetchSubCategories = useCallback(
        async (parentCategory: ICategory) => {
            setLoadingSubcategories(true);
            try {
                const url = buildRestUrl({
                    endpoint: RestEndpointsEnum.SubCategoriesGet,
                    params: { parentCategoryGuid: parentCategory.guid },
                });
                const response = await fetch(url, {
                    method: "GET",
                    headers: buildAuthHeadersForFetch({
                        userModel: props.userModel,
                        supplierModel: props.supplierModel,
                        vendorModel: props.vendorModel,
                    }),
                });
                const data: IOnGetSubCategoriesDTO = await response.json();
                setCurrentCategories(data.categories.filter((c) => !c.hide_category));
            } catch (error) {
                console.error("Error fetching subcategories", error);
                setCurrentCategories([]);
            } finally {
                setLoadingSubcategories(false);
            }
        },
        [props.userModel, props.supplierModel, props.vendorModel]
    );

    const handleDrillDown = (category: ICategory) => {
        if (category.has_dependents) {
            setNavigationPath((prev) => [...prev, category]);
            fetchSubCategories(category);
        }
    };

    const handleSelectCategory = (category: ICategory) => {
        // Build CategoryHierarchy from navigationPath for full path display
        const categoryWithHierarchy: ICategory = {
            ...category,
            CategoryHierarchy: [...navigationPath, category],
        };

        props.onChange(categoryWithHierarchy);
        handleClose();
    };

    const handleBreadcrumbClick = (index: number) => {
        if (index === -1) {
            // Go back to root
            setNavigationPath([]);
            setCurrentCategories(props.categories);
        } else {
            // Navigate to specific level
            const newPath = navigationPath.slice(0, index + 1);
            setNavigationPath(newPath);
            fetchSubCategories(newPath[newPath.length - 1]);
        }
    };

    const handleClose = () => {
        setIsOpen(false);
        // Reset navigation to root when closing
        setNavigationPath([]);
        setCurrentCategories(props.categories);
        setLoadingSubcategories(false);
    };

    const handleToggle = (nextShow: boolean) => {
        if (nextShow) {
            setIsOpen(true);
            // Reset to root categories when opening
            setNavigationPath([]);
            setCurrentCategories(props.categories);
        } else {
            handleClose();
        }
    };

    const handleClear = (e: React.MouseEvent) => {
        e.stopPropagation();
        props.onChange(undefined);
    };

    const getDisplayValue = (): string => {
        if (!props.value) {
            return "";
        }

        if (props.value.CategoryHierarchy && props.value.CategoryHierarchy.length > 0) {
            return props.value.CategoryHierarchy.map((c) => c.name).join(" > ");
        }

        return props.value.name;
    };

    const visibleCategories = currentCategories.filter((cat) => !cat.hide_category);

    return (
        <div className={`OwHierarchicalCategorySelector ${props.className || ""}`}>
            <Dropdown show={isOpen} onToggle={handleToggle}>
                <Dropdown.Toggle
                    as="div"
                    className={`selector-toggle form-control form-control-sm ${props.disabled ? "disabled" : ""}`}
                    aria-disabled={props.disabled}>
                    <div className="d-flex justify-content-between align-items-center w-100">
                        <span className={`selected-value ${!props.value ? "placeholder-text" : ""}`}>
                            {props.value ? (
                                getDisplayValue()
                            ) : (
                                <FormattedMessage id={props.placeholder || "select_category"} defaultMessage="Select Category" />
                            )}
                        </span>
                        <div className="toggle-icons d-flex align-items-center">
                            {props.value && !props.disabled && (
                                <i className="fas fa-times clear-icon ms-2 me-2" onClick={handleClear} title="Clear selection" />
                            )}
                            {/* <i className={`fas ${isOpen ? "fa-chevron-up" : "fa-chevron-down"}`} /> */}
                        </div>
                    </div>
                </Dropdown.Toggle>

                <Dropdown.Menu className="category-dropdown-menu">
                    {/* Breadcrumb Navigation */}
                    {navigationPath.length > 0 && (
                        <div className="breadcrumb-nav px-2 py-1 border-bottom">
                            <span className="breadcrumb-item pointer" onClick={() => handleBreadcrumbClick(-1)}>
                                <FormattedMessage id="all_categories" defaultMessage="All Categories" />
                            </span>
                            {navigationPath.map((cat, idx) => (
                                <React.Fragment key={cat.guid}>
                                    <i className="fas fa-chevron-right mx-1 small" />
                                    <span
                                        className={`breadcrumb-item ${idx === navigationPath.length - 1 ? "current" : "pointer"}`}
                                        onClick={() => idx < navigationPath.length - 1 && handleBreadcrumbClick(idx)}>
                                        {cat.name}
                                    </span>
                                </React.Fragment>
                            ))}
                        </div>
                    )}

                    {/* Loading Indicator */}
                    {loadingSubcategories && (
                        <div className="loading-indicator p-3 text-center">
                            <i className="fas fa-circle-notch fa-spin me-2" />
                            <FormattedMessage id="loading" defaultMessage="Loading..." />
                        </div>
                    )}

                    {/* Category List */}
                    {!loadingSubcategories && (
                        <div className="category-list">
                            {visibleCategories.length === 0 ? (
                                <div className="no-categories p-2 text-muted text-center">
                                    <FormattedMessage id="no_subcategories" defaultMessage="No subcategories" />
                                </div>
                            ) : (
                                visibleCategories.map((category) => (
                                    <div key={category.guid} className="category-item d-flex justify-content-between align-items-center">
                                        <span className="category-name flex-grow-1 pointer" onClick={() => handleSelectCategory(category)}>
                                            {category.name}
                                        </span>
                                        {category.has_dependents && (
                                            <i
                                                className="fas fa-chevron-right drill-down-icon pointer"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleDrillDown(category);
                                                }}
                                                title="View subcategories"
                                            />
                                        )}
                                    </div>
                                ))
                            )}
                        </div>
                    )}
                </Dropdown.Menu>
            </Dropdown>
        </div>
    );
};

export default OwHierarchicalCategorySelector;
